// 미리보기 컨텍스트 영속·stale 정리 — 순수 변환 모듈.
//
// 결정 기록: docs/decisions/2026-09-03-composer-simulation-persist-and-badge.md
// (2026-07-26 §⑥의 "모사 중임이 화면에 계속 보여야 한다" 원칙은 유지, 영속 정책만 개정)
//
// ## 왜 순수 함수인가
//
// 영속에서 **무엇이 빠지는가**는 규약이지 구현 세부가 아니다. `EditorPage`의 effect 안에
// `delete`로만 적혀 있으면 그 규약을 검사할 방법이 렌더 전체를 띄우는 것뿐이라, 조용히 뒤집혀도
// 아무도 모른다. 여기로 떼어 두면 되돌림 검사가 한 줄이다.

import type {
  PreviewSimulationAxis,
  ReportComposerPreviewContext,
} from '@safetyhub/shared';
import { clearPlanSimulationAxes } from './previewSimulationAxes';
import type { PreviewContextLabels } from './PreviewContextBar';

/**
 * localStorage에 넣을 형상 — **구 분할 선택(`splitPreview`)만** 뺀다.
 *
 * 모사 값(`previewSimulation`)은 **영속한다**(2026-09-03 개정). 저작 중 새로고침이 잦아
 * 그때마다 분할 그룹 축을 재추가(=부수효과 있는 계획 재조회)하는 비용이, 조용히 되살아난
 * 모사를 실데이터로 착각할 위험보다 컸다. 착각은 영속을 막아서가 아니라 **상시 모사 배너**로
 * 막는다(결정 §착각 방지). stale은 `releasePlanSimulation`이 사후에 정리한다.
 *
 * `splitPreview`는 그대로 제외한다 — admin에서 값이 실리는 경로가 아예 없고(오직 `undefined`로
 * 지워지기만 한다), 서버가 구 클라 호환으로만 읽는 필드다. 영속할 것이 원리적으로 없다.
 */
export function toPersistablePreviewContext(
  context: ReportComposerPreviewContext,
): ReportComposerPreviewContext {
  const persistable: ReportComposerPreviewContext = { ...context };
  delete persistable.splitPreview;
  return persistable;
}

/**
 * 지금 값이 실려 있는 `plan` 출처 축의 key들.
 *
 * 복원값 중 **stale해질 수 있는 것은 이 축뿐이다**: 계획 산출물(행 id)은 그 점검·그 시점의
 * 것이라 다음 세션 데이터에 그대로 있으리란 보장이 없고, 서버는 그걸 400("분할 대상에 없는
 * 항목입니다")으로 거절한다. 스칼라 모사(다회차 여부·날짜 등)는 서버가 값을 그대로 주입할 뿐이라
 * 원리적으로 그런 실패를 만들지 못한다 — 그래서 실패 귀속을 서버 응답 문구가 아니라 **축의
 * 성질**로 판정한다(서버는 plain 400이라 구분 가능한 에러 코드를 주지 않는다).
 */
export function planSourcedSimulationKeysWithValue(
  context: ReportComposerPreviewContext,
  axes: PreviewSimulationAxis[],
): string[] {
  const simulation = context.previewSimulation;
  if (!simulation) return [];
  return axes
    .filter((axis) => axis.optionsSource.includes('plan'))
    .filter((axis) => simulation[axis.key] !== undefined)
    .map((axis) => axis.key);
}

/**
 * `plan` 출처 축의 값 + 그 라벨만 걷어낸다(스칼라 모사·엔티티 선택은 그대로).
 *
 * 값과 라벨을 **함께** 지우는 이유: 라벨만 남으면 요약 칩·배너가 이미 풀린 축을 계속 모사 중인
 * 것처럼 말하고, 값만 남으면 opaque 값의 이름을 영영 잃는다(둘은 한 쌍으로만 뜻이 있다).
 */
export function releasePlanSimulation(
  context: ReportComposerPreviewContext,
  labels: PreviewContextLabels,
  axes: PreviewSimulationAxis[],
): {
  context: ReportComposerPreviewContext;
  labels: PreviewContextLabels;
  releasedKeys: string[];
} {
  const releasedKeys = planSourcedSimulationKeysWithValue(context, axes);
  if (releasedKeys.length === 0) return { context, labels, releasedKeys };

  const nextContext: ReportComposerPreviewContext = {
    ...context,
    previewSimulation: clearPlanSimulationAxes(context.previewSimulation, axes),
  };
  if (nextContext.previewSimulation === undefined) delete nextContext.previewSimulation;

  return {
    context: nextContext,
    labels: withoutSimulationLabels(labels, releasedKeys),
    releasedKeys,
  };
}

/** 모사 라벨 전체 제거(= 「전체 해제」). 엔티티 라벨은 건드리지 않는다. */
export function withoutAllSimulationLabels(
  labels: PreviewContextLabels,
): PreviewContextLabels {
  if (!labels.simulation) return labels;
  const next: PreviewContextLabels = { ...labels };
  delete next.simulation;
  return next;
}

/** 지정 축 라벨만 제거 — 남는 게 없으면 `simulation` 필드 자체를 없앤다(빈 맵을 남기지 않는다). */
function withoutSimulationLabels(
  labels: PreviewContextLabels,
  keys: string[],
): PreviewContextLabels {
  if (!labels.simulation) return labels;
  const simulation: Record<string, string> = { ...labels.simulation };
  for (const key of keys) delete simulation[key];
  const next: PreviewContextLabels = { ...labels };
  if (Object.keys(simulation).length > 0) next.simulation = simulation;
  else delete next.simulation;
  return next;
}

/**
 * 템플릿을 열 때 되살릴 미리보기 상태 — **엔티티는 항상, 모사는 `restoreSimulation`일 때만**.
 *
 * 모사 영속은 "같은 템플릿의 새로고침 생존" 전용이다(결정 2026-09-03 §영속의 범위). 목록에서 다른
 * 템플릿을 고르는 것은 갈래를 이어 보겠다는 뜻이 아니라 **다른 문서를 여는 것**이라, 그 템플릿의
 * 과거 저장분이 있어도 되살리지 않는다. 반대로 엔티티 선택("어느 데이터로 보는가")은 전환과 무관해
 * 두 경우 모두 복원한다.
 *
 * 값과 라벨을 한 함수가 함께 정하는 이유: 둘은 한 쌍이라 따로 두면 어긋난다. 값을 안 되살렸는데
 * 라벨만 남으면 상시 배너가 **이미 없는 모사를 말하고**, 반대면 opaque 값의 이름을 잃는다.
 */
export function restoredPreviewStateFor(args: {
  savedContext: ReportComposerPreviewContext;
  savedLabels: PreviewContextLabels;
  restoreSimulation: boolean;
}): { context: ReportComposerPreviewContext; labels: PreviewContextLabels } {
  const { savedContext, savedLabels, restoreSimulation } = args;
  if (restoreSimulation) return { context: savedContext, labels: savedLabels };
  return {
    context: clearAllSimulation(savedContext),
    labels: withoutAllSimulationLabels(savedLabels),
  };
}

/**
 * 엔티티 선택(조직·현장·점검·계정)이 달라졌는가 — 모사 값은 보지 않는다.
 *
 * 모사를 "아직 이 데이터에서 검증되지 않은 값"으로 표시할 시점을 고르는 데 쓴다. 엔티티가 바뀌면
 * 계획 산출물(행 id)이 다른 점검의 것이 되어 stale일 수 있지만, **미리 지우지는 않는다**(결정
 * 2026-09-03 보강: 엔티티 변경 시 모사는 유지하고, 실패하면 그때 그 축만 해제한다). 모사만 바꾼
 * 변경까지 "미검증"으로 표시하면, 저작자가 방금 고른 갈래가 무관한 실패 한 번에 풀린다.
 */
export function previewEntitiesChanged(
  a: ReportComposerPreviewContext,
  b: ReportComposerPreviewContext,
): boolean {
  return (
    a.companyId !== b.companyId ||
    a.siteId !== b.siteId ||
    a.inspectionId !== b.inspectionId ||
    a.userId !== b.userId ||
    (a.inspectionIds ?? []).join(',') !== (b.inspectionIds ?? []).join(',')
  );
}

/** 모사 값 전체 해제 — 엔티티 선택은 유지한다(「전체 해제」 버튼이 쓰는 변환). */
export function clearAllSimulation(
  context: ReportComposerPreviewContext,
): ReportComposerPreviewContext {
  if (context.previewSimulation === undefined) return context;
  const next: ReportComposerPreviewContext = { ...context };
  delete next.previewSimulation;
  return next;
}
